import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import PieChartWidget from "../main/PieChartWidget";
import { prepareOverloadSharePie, prepareOverloadTypePie } from "../widgets/prepareOverloadPieData";
import ToggleSection, { useToggleSection } from "../../../widget/ToggleSection";
import ToggleTagGroupPanel from "./ToggleTagGroupPanel";
import {
  toggleExcludedSiteDirectionActive as toggleExcludedSiteDirection,
  includeSelectedSiteDirectionsActive as includeSelectedSiteDirections,
  excludeSelectedSiteDirectionsActive as excludeSelectedSiteDirections,
  setIncludedSiteDirectionsActive as setIncludedSiteDirections,
  setExcludedSiteDirectionsActive as setExcludedSiteDirections,
} from "../../../../state/facade/dataFacade";
import {selectDataMode} from "../../../../state/slice/dataModeSlice";
import {selectSitesAgg} from "../../../../state/facade/statisticsFacade";

const OverloadsByType = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dataSourceMode = useSelector(selectDataMode);

  const { data: statsPayload = {}, loading } = useSelector(selectSitesAgg);
  const { isHidden, togglerKey } = useToggleSection(["footer"], ["footer"]);


  const excludedSiteDirectionNames = useSelector((s) =>
      (dataSourceMode === "processed"
          ? s.processedMapFiltersSlice.excluded.siteDirections
          : s.mapFiltersSlice.excluded.siteDirections) || []
  );
  // enumerate all siteDirections from payload (stable alpha order)
  const allSiteDirectionItems = useMemo(() => {
    const set = new Set();
    Object.values(statsPayload || {}).forEach((bundle) => {
      (bundle?.dailyStats || []).forEach((row) => {
        if (row?.siteDirectionName) set.add(String(row.siteDirectionName));
      });
    });
    return Array.from(set)
    .map((name) => ({ name }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [statsPayload]);

  const excludedSiteDirectionItems = useMemo(
      () => excludedSiteDirectionNames.map((name) => ({ name })),
      [excludedSiteDirectionNames]
  );

  // Pies (respect siteDirection excludes; site & direction level lists unused here)
  const overloadShare = useMemo(
      () =>
          prepareOverloadSharePie(
              statsPayload,
              [], // sites
              [], // directions
              excludedSiteDirectionNames // siteDirectionNames
          ),
      [statsPayload, excludedSiteDirectionNames]
  );

  const overloadType = useMemo(
      () =>
          prepareOverloadTypePie(
              statsPayload,
              [], // sites
              [], // directions
              excludedSiteDirectionNames // siteDirectionNames
          ),
      [statsPayload, excludedSiteDirectionNames]
  );

  return (
      <>
        <div className="card-content">
          <div className="columns">
            <div className="column">
              <PieChartWidget
                  id="pie-overload-share"
                  title="dashboards_view.kpi1.pie_overload_share.title"
                  labels={overloadShare.labels}
                  values={overloadShare.values}
                  isLoading={loading}
                  colors={overloadShare.colors}
              />
            </div>
            <div className="column">
              <PieChartWidget
                  id="pie-overload-types"
                  title="dashboards_view.kpi1.pie_overload_types.title"
                  labels={overloadType.labels}
                  values={overloadType.values}
                  isLoading={loading}
                  colors={overloadType.colors}
              />
            </div>
          </div>
        </div>

        <ToggleSection
            sectionKey="footer"
            isHidden={isHidden}
            onToggle={togglerKey}
            title={t("dashboards.tabs.PKI3_content.filter")}
        >
          <ToggleTagGroupPanel
              label={t("dashboards_view.kpi3.site_direction_selection")}
              allItems={allSiteDirectionItems}
              excludedItems={excludedSiteDirectionItems}
              id="name"
              labelKeys={["name"]}
              onToggleItem={(item) => dispatch(toggleExcludedSiteDirection(item.name))}
              onIncludeAllSelectedFromExclude={(items) =>
                  dispatch(includeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onExcludeAllSelectedFromInclude={(items) =>
                  dispatch(excludeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onFlipSelection={() => {
                const all = allSiteDirectionItems.map((i) => i.name);
                const excluded = new Set(excludedSiteDirectionNames);
                const toInclude = all.filter((n) => excluded.has(n));
                const toExclude = all.filter((n) => !excluded.has(n));
                dispatch(setIncludedSiteDirections(toExclude));
                dispatch(setExcludedSiteDirections(toInclude));
              }}
          />
        </ToggleSection>
      </>
  );
};

export default OverloadsByType;
